import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tradesService } from '@/services/trades/trades-service';
import { tradesKeys } from '@/services/query-keys/trades-keys';
import { invalidateTradeAndPoolCaches } from './invalidate-trade-caches';

/**
 * LR-470: settle a pending partial recall/return (manual operator action — the
 * click marks when settlement actually happened; there is no auto-settle).
 * Reduces the booked quantity and reprices earnings/CV, so invalidate the whole
 * positions cache — the trades list and the MTM page both read from it.
 * LR-515: an optional settlementDate overrides the stored contractual date
 * atomically at settle (the dialog pre-fills it so the operator can adjust before
 * posting the BO leg).
 */
export const useSettlePartialRecall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      recallId,
      settlementDate,
    }: {
      positionId: number;
      recallId: number;
      settlementDate?: string;
    }) => tradesService.settlePartialRecall(positionId, recallId, settlementDate),
    onSuccess: () => {
      // LR-586: settling a return takes its collateral out of the pool. The
      // cancel/date-override hooks below move no cash, so they stay trades-only.
      invalidateTradeAndPoolCaches(queryClient);
    },
  });
};

/**
 * LR-470: cancel a pending partial recall/return before it settles. Restores
 * the MTM-markable remaining quantity; no Backoffice posting.
 */
export const useCancelPartialRecall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ positionId, recallId }: { positionId: number; recallId: number }) =>
      tradesService.cancelPartialRecall(positionId, recallId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradesKeys.all });
    },
  });
};

/**
 * LR-470: override the contractual settlement date of a pending partial
 * recall/return (so billing stays correct when settlement is delayed). Only
 * moves the date — no quantity change, no Backoffice posting.
 */
export const useSetPartialRecallSettlementDate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      recallId,
      settlementDate,
    }: {
      positionId: number;
      recallId: number;
      settlementDate: string;
    }) => tradesService.setPartialRecallSettlementDate(positionId, recallId, settlementDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradesKeys.all });
    },
  });
};
